<template>
  <!-- 仅在已登录的页面显示 -->
  <div v-if="visible" class="float-toolbar">
    <!-- 工具按钮 -->
    <div class="toolbar-buttons">
      <div class="toolbar-btn" @click="scrollToTop" title="回到顶部">
        <el-icon :size="20"><Top /></el-icon>
        <span class="btn-label">顶部</span>
      </div>
      <div class="toolbar-btn" @click="toggleChat" title="在线客服" :class="{ active: chatOpen }">
        <el-icon :size="20"><Service /></el-icon>
        <span class="btn-label">客服</span>
        <span class="unread-dot" v-if="unreadCount > 0"></span>
      </div>
      <div class="toolbar-btn" @click="goTo('/cart')" title="购物车">
        <el-icon :size="20"><ShoppingCart /></el-icon>
        <span class="btn-label">购物车</span>
        <span class="cart-badge" v-if="cartCount > 0">{{ cartCount > 99 ? '99+' : cartCount }}</span>
      </div>
      <div class="toolbar-btn" @click="goTo('/orders')" title="我的订单">
        <el-icon :size="20"><List /></el-icon>
        <span class="btn-label">订单</span>
      </div>
      <div class="toolbar-btn coupon-btn" :class="{ claimed: hasCoupon }" @click="showCoupon = true" title="优惠券">
        <el-icon :size="20"><Ticket /></el-icon>
        <span class="btn-label">优惠券</span>
        <span class="coupon-dot" v-if="hasCoupon"></span>
      </div>
    </div>

    <!-- 客服聊天窗口 -->
    <transition name="chat-slide">
      <div v-if="chatOpen" class="chat-window glassmorphism">
        <div class="chat-header">
          <div class="chat-header-left">
            <div class="cs-avatar">
              <el-icon :size="18" color="#fff"><Service /></el-icon>
            </div>
            <div>
              <h4>在线客服</h4>
              <span class="cs-status"><span class="status-dot"></span>在线</span>
            </div>
          </div>
          <button class="chat-close" @click="chatOpen = false">
            <el-icon :size="16"><Close /></el-icon>
          </button>
        </div>
        <div class="chat-body" ref="chatBody">
          <div
            v-for="(msg, index) in messages"
            :key="index"
            class="chat-msg"
            :class="msg.from"
          >
            <div class="msg-avatar" v-if="msg.from === 'cs'">
              <el-icon :size="14" color="#fff"><Service /></el-icon>
            </div>
            <div class="msg-bubble">
              <p v-html="msg.text"></p>
              <span class="msg-time">{{ msg.time }}</span>
            </div>
            <div class="msg-avatar user-avatar" v-if="msg.from === 'user'">
              <el-icon :size="14" color="#fff"><User /></el-icon>
            </div>
          </div>
          <div v-if="csTyping" class="chat-msg cs">
            <div class="msg-avatar">
              <el-icon :size="14" color="#fff"><Service /></el-icon>
            </div>
            <div class="msg-bubble typing">
              <span class="dot"></span><span class="dot"></span><span class="dot"></span>
            </div>
          </div>
        </div>
        <div class="chat-quick-replies">
          <button
            v-for="(q, i) in quickReplies"
            :key="i"
            class="quick-btn"
            @click="sendQuick(q)"
          >{{ q }}</button>
        </div>
        <div class="chat-footer">
          <input
            v-model="inputMsg"
            placeholder="输入消息..."
            @keyup.enter="sendMessage"
            class="chat-input"
          />
          <button class="chat-send" @click="sendMessage" :disabled="!inputMsg.trim()">
            <el-icon :size="18"><Promotion /></el-icon>
          </button>
        </div>
      </div>
    </transition>

  </div>

  <!-- 优惠券弹窗 - Teleport 到 body 确保全屏居中 -->
  <Teleport to="body">
    <transition name="coupon-fade">
      <div v-if="showCoupon" class="coupon-overlay" @click.self="showCoupon = false">
        <div class="coupon-modal">
          <div class="coupon-modal-header">
            <div class="coupon-modal-title">
              <el-icon :size="22" color="#ff9500"><Ticket /></el-icon>
              <h3>领取优惠券</h3>
            </div>
            <button class="coupon-modal-close" @click="showCoupon = false">
              <el-icon :size="18"><Close /></el-icon>
            </button>
          </div>
          <div class="coupon-modal-body">
            <div class="coupon-card-center" :class="{ 'already-claimed': hasCoupon }">
              <div class="coupon-badge">限时</div>
              <div class="coupon-card-left">
                <span class="coupon-discount">9<small>折</small></span>
                <span class="coupon-scope">全场通用</span>
              </div>
              <div class="coupon-divider">
                <div class="divider-dot top"></div>
                <div class="divider-line"></div>
                <div class="divider-dot bottom"></div>
              </div>
              <div class="coupon-card-right">
                <h4>新用户9折优惠券</h4>
                <p class="coupon-desc">全场商品可用，无门槛</p>
                <p class="coupon-validity">有效期至 2026-12-31</p>
                <button
                  class="claim-btn"
                  :class="{ claimed: hasCoupon }"
                  @click="claimCoupon"
                  :disabled="hasCoupon"
                >
                  <el-icon v-if="hasCoupon" :size="14"><Select /></el-icon>
                  {{ hasCoupon ? '已领取' : '立即领取' }}
                </button>
              </div>
            </div>
          </div>
          <div class="coupon-modal-footer">
            <span class="coupon-tip">更多优惠活动敬请期待 ~</span>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import {
  Top, Service, ShoppingCart, List, Ticket, Close,
  User, Promotion, Select
} from '@element-plus/icons-vue'

const router = useRouter()
const route = useRoute()
const chatOpen = ref(false)
const showCoupon = ref(false)
const inputMsg = ref('')
const chatBody = ref(null)
const csTyping = ref(false)
const unreadCount = ref(0)
const cartCount = ref(0)
const hasCoupon = ref(false)

// 判断是否显示（登录页/注册页/商家端/管理端不显示）
const hiddenRoutes = ['/login', '/register', '/merchant/login', '/admin/login']
const visible = computed(() => {
  if (hiddenRoutes.includes(route.path)) return false
  if (route.path.startsWith('/merchant')) return false
  if (route.path.startsWith('/admin')) return false
  return true
})

// 快捷回复
const quickReplies = ['查询订单', '物流问题', '退换货', '优惠活动']

// 消息列表
const messages = ref([
  {
    from: 'cs',
    text: '您好！欢迎来到云商城在线客服 😊<br>请问有什么可以帮助您的？',
    time: formatNow(),
  },
])

// 初始化优惠券领取状态
try {
  hasCoupon.value = localStorage.getItem('coupon_claimed') === 'true'
} catch {}

// 客服自动回复库
const autoReplies = {
  '查询订单': '您可以在「我的订单」页面查看所有订单状态。如果需要查询特定订单，请提供订单号，我来帮您查询~',
  '物流问题': '物流通常在下单后1-3天内发货，发货后3-7个工作日送达。如果超时未收到，请提供订单号，我帮您催件~',
  '退换货': '云商城支持7天无理由退换货。请在「我的订单」中找到对应订单，点击"申请售后"即可发起退换货申请。',
  '优惠活动': '当前我们有以下活动：<br>🎉 新人注册送10元优惠券<br>🔥 满200减30<br>⭐ 会员日专享9折<br>赶快去选购吧！',
}

const defaultReplies = [
  '好的，我已记录您的问题，会尽快为您处理~',
  '感谢您的反馈！如有其他问题，随时联系我们。',
  '请稍等，让我帮您查看一下...',
  '您好，这个问题我需要帮您转接专业客服，请稍候~',
  '收到！您的问题已提交，预计1-2分钟内给您回复。',
]

function formatNow() {
  const d = new Date()
  const pad = n => String(n).padStart(2, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** 回到顶部 */
const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

/** 路由跳转 */
const goTo = (path) => {
  router.push(path)
}

/** 切换客服窗口 */
const toggleChat = () => {
  chatOpen.value = !chatOpen.value
  if (chatOpen.value) {
    unreadCount.value = 0
    nextTick(scrollChatBottom)
  }
}

/** 发送消息 */
const sendMessage = () => {
  const text = inputMsg.value.trim()
  if (!text) return
  messages.value.push({ from: 'user', text, time: formatNow() })
  inputMsg.value = ''
  nextTick(scrollChatBottom)

  // 自动回复
  csTyping.value = true
  const delay = 800 + Math.random() * 1200
  setTimeout(() => {
    csTyping.value = false
    let reply = autoReplies[text]
    if (!reply) {
      reply = defaultReplies[Math.floor(Math.random() * defaultReplies.length)]
    }
    messages.value.push({ from: 'cs', text: reply, time: formatNow() })
    if (!chatOpen.value) unreadCount.value++
    nextTick(scrollChatBottom)
  }, delay)
}

/** 快捷回复 */
const sendQuick = (q) => {
  inputMsg.value = q
  sendMessage()
}

/** 领取优惠券 */
const claimCoupon = () => {
  if (hasCoupon.value) return
  hasCoupon.value = true
  localStorage.setItem('coupon_claimed', 'true')
  ElMessage.success('🎉 恭喜！成功领取9折优惠券')
}

/** 滚动聊天到底部 */
const scrollChatBottom = () => {
  if (chatBody.value) {
    chatBody.value.scrollTop = chatBody.value.scrollHeight
  }
}

/** 更新购物车数量 */
const updateCartCount = () => {
  try {
    const data = JSON.parse(localStorage.getItem('cart') || '[]')
    cartCount.value = data.reduce((s, i) => s + (i.quantity || 1), 0)
  } catch { cartCount.value = 0 }
}

// 定时更新购物车数量
let cartTimer = null
onMounted(() => {
  updateCartCount()
  cartTimer = setInterval(updateCartCount, 2000)
})
onUnmounted(() => {
  if (cartTimer) clearInterval(cartTimer)
})
</script>

<style scoped>
/* ========== 浮动工具栏 ========== */
.float-toolbar {
  position: fixed;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}

.toolbar-buttons {
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(31, 38, 135, 0.12);
  padding: 8px 6px;
}

.toolbar-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  color: #555;
}

.toolbar-btn:hover {
  background: rgba(83, 124, 229, 0.1);
  color: #537ce5;
  transform: scale(1.08);
}

.toolbar-btn.active {
  background: linear-gradient(135deg, #537ce5 0%, #8b5fc9 100%);
  color: #fff;
}

.btn-label {
  font-size: 0.6rem;
  margin-top: 2px;
  line-height: 1;
  font-weight: 500;
}

.unread-dot {
  position: absolute;
  top: 4px;
  right: 6px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ff4d4f;
  border: 2px solid #fff;
}

.cart-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: #ff4d4f;
  color: #fff;
  font-size: 0.6rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.coupon-btn:hover {
  color: #ff9500 !important;
  background: rgba(255, 149, 0, 0.1) !important;
}

/* ========== 客服窗口 ========== */
.chat-window {
  position: fixed;
  right: 80px;
  bottom: 24px;
  width: 360px;
  height: 520px;
  max-height: calc(100vh - 120px);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 16px 48px rgba(31, 38, 135, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.glassmorphism {
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
}

.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: linear-gradient(135deg, #232a4d 0%, #4b2067 100%);
  color: #fff;
}

.chat-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cs-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #537ce5 0%, #8b5fc9 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.chat-header h4 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
}

.cs-status {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.72rem;
  opacity: 0.85;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #43b581;
  display: inline-block;
}

.chat-close {
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 8px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
  transition: background 0.2s;
}

.chat-close:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* ========== 聊天内容 ========== */
.chat-body {
  flex: 1;
  padding: 16px 14px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.chat-body::-webkit-scrollbar {
  width: 4px;
}

.chat-body::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 2px;
}

.chat-msg {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  max-width: 85%;
}

.chat-msg.cs {
  align-self: flex-start;
}

.chat-msg.user {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.msg-avatar {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #537ce5 0%, #8b5fc9 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.msg-avatar.user-avatar {
  background: linear-gradient(135deg, #232a4d 0%, #4b2067 100%);
}

.msg-bubble {
  padding: 10px 14px;
  border-radius: 14px;
  font-size: 0.88rem;
  line-height: 1.5;
  position: relative;
}

.msg-bubble p {
  margin: 0;
}

.cs .msg-bubble {
  background: #f0f2f5;
  color: #333;
  border-bottom-left-radius: 4px;
}

.user .msg-bubble {
  background: linear-gradient(135deg, #537ce5 0%, #8b5fc9 100%);
  color: #fff;
  border-bottom-right-radius: 4px;
}

.msg-time {
  display: block;
  font-size: 0.65rem;
  opacity: 0.5;
  margin-top: 4px;
  text-align: right;
}

/* 正在输入动画 */
.msg-bubble.typing {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 18px;
}

.msg-bubble.typing .dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #999;
  animation: typingBounce 1.4s infinite;
}

.msg-bubble.typing .dot:nth-child(2) { animation-delay: 0.2s; }
.msg-bubble.typing .dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes typingBounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-6px); opacity: 1; }
}

/* ========== 快捷回复 ========== */
.chat-quick-replies {
  display: flex;
  gap: 6px;
  padding: 8px 14px;
  flex-wrap: wrap;
  border-top: 1px solid rgba(0, 0, 0, 0.04);
}

.quick-btn {
  padding: 4px 12px;
  border: 1px solid rgba(83, 124, 229, 0.2);
  border-radius: 14px;
  background: rgba(83, 124, 229, 0.06);
  color: #537ce5;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.quick-btn:hover {
  background: #537ce5;
  color: #fff;
  border-color: #537ce5;
}

/* ========== 输入区 ========== */
.chat-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.6);
}

.chat-input {
  flex: 1;
  padding: 8px 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  font-size: 0.88rem;
  outline: none;
  transition: border-color 0.3s;
  background: #fff;
}

.chat-input:focus {
  border-color: #537ce5;
}

.chat-send {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #537ce5 0%, #8b5fc9 100%);
  color: #fff;
  cursor: pointer;
  transition: all 0.3s;
  flex-shrink: 0;
}

.chat-send:hover:not(:disabled) {
  transform: scale(1.08);
  box-shadow: 0 4px 12px rgba(83, 124, 229, 0.4);
}

.chat-send:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ========== 动画 ========== */
.chat-slide-enter-active { animation: chatIn 0.35s ease; }
.chat-slide-leave-active { animation: chatOut 0.25s ease; }

@keyframes chatIn {
  from { opacity: 0; transform: translateY(20px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes chatOut {
  from { opacity: 1; transform: translateY(0) scale(1); }
  to { opacity: 0; transform: translateY(20px) scale(0.95); }
}

/* 优惠券弹窗动画 - 保留在scoped中供toolbar按钮使用 */

/* ========== 响应式 ========== */
@media (max-width: 768px) {
  .float-toolbar { right: 8px; }
  .toolbar-btn { width: 44px; height: 44px; }
  .btn-label { font-size: 0.55rem; }

  .chat-window {
    right: 8px;
    bottom: 16px;
    width: calc(100vw - 80px);
    max-width: 360px;
    height: 460px;
    max-height: calc(100vh - 96px);
  }
}
</style>

<!-- 非 scoped 样式：Teleport 到 body 的优惠券弹窗 -->
<style>
.coupon-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
}

.coupon-modal {
  width: 460px;
  max-width: 90vw;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  animation: couponPop 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes couponPop {
  from { opacity: 0; transform: scale(0.85) translateY(20px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}

.coupon-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  background: linear-gradient(135deg, #232a4d 0%, #4b2067 100%);
  color: #fff;
}

.coupon-modal-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.coupon-modal-title h3 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
}

.coupon-modal-close {
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
  transition: background 0.2s;
}

.coupon-modal-close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.coupon-modal-body {
  padding: 28px 24px;
}

.coupon-card-center {
  display: flex;
  align-items: stretch;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(255, 149, 0, 0.15);
  border: 2px solid rgba(255, 149, 0, 0.2);
  position: relative;
  transition: all 0.3s;
}

.coupon-card-center.already-claimed {
  border-color: rgba(67, 181, 129, 0.3);
  box-shadow: 0 4px 20px rgba(67, 181, 129, 0.12);
}

.coupon-badge {
  position: absolute;
  top: 0;
  left: 0;
  padding: 2px 12px;
  background: linear-gradient(135deg, #ff6b6b, #ff4d4f);
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  border-bottom-right-radius: 10px;
  z-index: 1;
}

.coupon-card-left {
  width: 130px;
  min-width: 130px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 12px;
  background: linear-gradient(135deg, #ff9500, #ffb347);
  color: #fff;
}

.already-claimed .coupon-card-left {
  background: linear-gradient(135deg, #43b581, #2e8b57);
}

.coupon-discount {
  font-size: 3rem;
  font-weight: 900;
  line-height: 1;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.coupon-discount small {
  font-size: 1.2rem;
  font-weight: 700;
}

.coupon-scope {
  font-size: 0.78rem;
  margin-top: 6px;
  opacity: 0.9;
  font-weight: 500;
}

.coupon-divider {
  width: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
}

.divider-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #fff;
  position: relative;
  z-index: 1;
}

.divider-dot.top { margin-top: -7px; }
.divider-dot.bottom { margin-bottom: -7px; }

.divider-line {
  flex: 1;
  width: 0;
  border-left: 2px dashed rgba(0, 0, 0, 0.08);
}

.coupon-card-right {
  flex: 1;
  padding: 20px 20px 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  background: #fff;
}

.coupon-card-right h4 {
  margin: 0;
  font-size: 1.05rem;
  color: #232a4d;
  font-weight: 700;
}

.coupon-desc {
  font-size: 0.82rem;
  color: #666;
  margin: 0;
}

.coupon-validity {
  font-size: 0.75rem;
  color: #aaa;
  margin: 0;
}

.claim-btn {
  align-self: flex-end;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 24px;
  border: none;
  border-radius: 20px;
  background: linear-gradient(135deg, #ff9500, #ffb347);
  color: #fff;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 6px;
  letter-spacing: 1px;
}

.claim-btn:hover:not(:disabled) {
  transform: scale(1.06);
  box-shadow: 0 6px 16px rgba(255, 149, 0, 0.35);
}

.claim-btn.claimed {
  background: linear-gradient(135deg, #43b581, #2e8b57);
  cursor: default;
  letter-spacing: 0;
}

.claim-btn:disabled {
  opacity: 0.95;
}

.coupon-modal-footer {
  padding: 14px 24px;
  text-align: center;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  background: #fafafa;
  border-radius: 0 0 20px 20px;
}

.coupon-modal-footer .coupon-tip {
  font-size: 0.82rem;
  color: #999;
}

/* 优惠券弹窗动画 */
.coupon-fade-enter-active { transition: opacity 0.25s ease; }
.coupon-fade-leave-active { transition: opacity 0.2s ease; }
.coupon-fade-enter-from,
.coupon-fade-leave-to { opacity: 0; }
</style>
